import { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { LangProvider } from '@/i18n/LangContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { AnalyticsPageViewTracker, CookieConsent } from '@/components/CookieConsent';
import Home from '@/pages/Home';
import Soluzioni from '@/pages/Soluzioni';
import ContentPage from '@/pages/ContentPage';
import ChiSiamo from '@/pages/ChiSiamo';
import Missione from '@/pages/Missione';
import CentroSinistri from '@/pages/CentroSinistri';
import Contatti from '@/pages/Contatti';
import GuideIndex from '@/pages/GuideIndex';
import TaxSavingsPage from '@/pages/TaxSavingsPage';
import LamalCalculatorPage from '@/pages/LamalCalculatorPage';
import LegalPage from '@/pages/LegalPage';
import NotFound from '@/pages/NotFound';
import AppointmentBooking from '@/pages/AppointmentBooking';
import AppointmentSurvey from '@/pages/AppointmentSurvey';
import { ReferralCommissions, ReferralConditions, ReferralLanding } from '@/pages/ReferralPages';
import CollaborationAgreement from '@/pages/CollaborationAgreement';

let smoothScrollInstance = null;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    smoothScrollInstance?.scrollTo(0, { immediate: true, force: true });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
};

const LegacyHealthRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const destination = pathname.replace(/^\/soluzioni\/salute|^\/salute/, '/cassa-malati');
  return <Navigate to={`${destination}${search}${hash}`} replace />;
};

const SmoothScroll = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    smoothScrollInstance = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      if (smoothScrollInstance === lenis) smoothScrollInstance = null;
      lenis.destroy();
    };
  }, []);
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <SmoothScroll />
      <ScrollToTop />
      <LangProvider>
        <AnalyticsPageViewTracker />
        <CookieConsent />
        <div className="flex flex-col">
          <div className="flex min-h-screen flex-col">
            <Header />
            <main className="flex flex-1 flex-col">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/soluzioni" element={<Soluzioni />} />
              <Route path="/salute/*" element={<LegacyHealthRedirect />} />
              <Route path="/soluzioni/salute/*" element={<LegacyHealthRedirect />} />
              <Route path="/chi-siamo" element={<ChiSiamo />} />
              <Route path="/chi-siamo/missione" element={<Missione />} />
              <Route path="/centro-sinistri" element={<CentroSinistri />} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/prenota" element={<AppointmentBooking />} />
              <Route path="/appuntamento/feedback/:publicToken" element={<AppointmentSurvey />} />
              <Route path="/consiglio-a-terzi" element={<ReferralLanding />} />
              <Route path="/consiglio-a-terzi/commissioni" element={<ReferralCommissions />} />
              <Route path="/consiglio-a-terzi/condizioni" element={<ReferralConditions />} />
              <Route path="/accordo-collaborazione-indicatori" element={<CollaborationAgreement />} />
              <Route path="/guide" element={<GuideIndex />} />
              <Route path="/calcola-risparmio-fiscale" element={<TaxSavingsPage />} />
              <Route path="/cassa-malati/calcolatore-premi" element={<LamalCalculatorPage />} />
              <Route path="/privacy" element={<LegalPage />} />
              <Route path="/cookie-policy" element={<LegalPage />} />
              <Route path="/note-legali" element={<LegalPage />} />
              <Route path="/soluzioni/*" element={<ContentPage />} />
              <Route path="/cassa-malati/*" element={<ContentPage />} />
              <Route path="/guide/*" element={<ContentPage />} />

              <Route path="/en" element={<Home />} />
              <Route path="/en/solutions" element={<Soluzioni />} />
              <Route path="/en/about" element={<ChiSiamo />} />
              <Route path="/en/about/mission" element={<Missione />} />
              <Route path="/en/claims-centre" element={<CentroSinistri />} />
              <Route path="/en/contact" element={<Contatti />} />
              <Route path="/en/book" element={<AppointmentBooking />} />
              <Route path="/en/refer-someone" element={<ReferralLanding />} />
              <Route path="/en/refer-someone/rewards" element={<ReferralCommissions />} />
              <Route path="/en/refer-someone/conditions" element={<ReferralConditions />} />
              <Route path="/en/accordo-collaborazione-indicatori" element={<CollaborationAgreement />} />
              <Route path="/en/guides" element={<GuideIndex />} />
              <Route path="/en/tax-savings-calculator" element={<TaxSavingsPage />} />
              <Route path="/en/health-insurance-premium-calculator" element={<LamalCalculatorPage />} />
              <Route path="/en/privacy" element={<LegalPage />} />
              <Route path="/en/cookie-policy" element={<LegalPage />} />
              <Route path="/en/legal-notice" element={<LegalPage />} />
              <Route path="/en/solutions/*" element={<ContentPage />} />
              <Route path="/en/guides/*" element={<ContentPage />} />

              <Route path="/404" element={<NotFound />} />
              <Route path="/en/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </main>
          </div>
          <Footer />
        </div>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
